import api from './api';
import type {
  CreateWebhookData,
  UpdateWebhookData,
  WebhookDelivery,
  WebhookDeliveryPage,
  WebhookEndpoint,
  WebhookEndpointWithSecret,
  WebhookEventCatalog,
  WebhookTestResult,
} from '../types';

export const webhookService = {
  /**
   * The full catalog of events the platform can emit. Drives the subscription
   * picker so the UI never drifts from what the API actually sends.
   */
  async getEventCatalog(): Promise<WebhookEventCatalog> {
    const response = await api.get<WebhookEventCatalog>('/company/webhooks/event-types');
    return response.data;
  },

  async getWebhooks(): Promise<WebhookEndpoint[]> {
    const response = await api.get<WebhookEndpoint[]>('/company/webhooks');
    return response.data;
  },

  /**
   * Creates an endpoint. The response contains the plaintext signing secret —
   * it is only readable here, so surface it to the user immediately.
   */
  async createWebhook(data: CreateWebhookData): Promise<WebhookEndpointWithSecret> {
    const response = await api.post<WebhookEndpointWithSecret>('/company/webhooks', data);
    return response.data;
  },

  async updateWebhook(webhookId: string, data: UpdateWebhookData): Promise<WebhookEndpoint> {
    const response = await api.patch<WebhookEndpoint>(`/company/webhooks/${webhookId}`, data);
    return response.data;
  },

  async deleteWebhook(webhookId: string): Promise<{ success: boolean }> {
    const response = await api.delete<{ success: boolean }>(`/company/webhooks/${webhookId}`);
    return response.data;
  },

  /** Issues a new signing secret. The previous one stops working immediately. */
  async rotateSecret(webhookId: string): Promise<{ secret: string }> {
    const response = await api.post<{ secret: string }>(`/company/webhooks/${webhookId}/rotate-secret`);
    return response.data;
  },

  /** Sends a synthetic event and waits for the endpoint's response. */
  async sendTestEvent(webhookId: string, eventType?: string): Promise<WebhookTestResult> {
    const response = await api.post<WebhookTestResult>(`/company/webhooks/${webhookId}/test`, { eventType });
    return response.data;
  },

  async getDeliveries(params: {
    webhookId?: string;
    status?: string;
    eventType?: string;
    page?: number;
    pageSize?: number;
  } = {}): Promise<WebhookDeliveryPage> {
    const response = await api.get<WebhookDeliveryPage>('/company/webhooks/deliveries', { params });
    return response.data;
  },

  async getDelivery(deliveryId: string): Promise<WebhookDelivery> {
    const response = await api.get<WebhookDelivery>(`/company/webhooks/deliveries/${deliveryId}`);
    return response.data;
  },

  async redeliver(deliveryId: string): Promise<WebhookDelivery> {
    const response = await api.post<WebhookDelivery>(`/company/webhooks/deliveries/${deliveryId}/redeliver`);
    return response.data;
  },
};
